import { useCallback } from 'react'
import { useAccount, useDisconnect } from 'wagmi'
import { switchChain } from '@wagmi/core'
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react'
import { wagmiConfig } from '@/config/wagmi'

export interface UseWalletReturn {
  evmAddress: string | undefined
  evmChainId: number | undefined
  isEvmConnected: boolean
  evmConnectorName: string | undefined
  solanaAddress: string | undefined
  isSolanaConnected: boolean
  solanaWalletName: string | undefined
  isConnecting: boolean
  connectEvm: () => void
  connectSolana: () => void
  disconnectEvm: () => void
  disconnectSolana: () => void
  switchEvmChain: (chainId: number) => Promise<void>
}

export function useWallet(): UseWalletReturn {
  // EVM state from wagmi
  const { address: evmAddress, chainId: evmChainId, isConnected: isEvmConnected, isConnecting: isEvmConnecting, connector } = useAccount()
  const { disconnect: wagmiDisconnect } = useDisconnect()

  // Solana state from wallet-adapter
  const {
    publicKey: solanaPublicKey,
    connected: isSolanaConnected,
    connecting: isSolanaConnecting,
    disconnect: solanaDisconnect,
    wallet: solanaWallet,
  } = useSolanaWallet()

  const solanaAddress = solanaPublicKey?.toBase58()
  const isConnecting = isEvmConnecting || isSolanaConnecting

  // Connect stubs — actual connection happens via WalletModal using useConnect/select()
  // These are no-ops; the modal drives connection
  const connectEvm = useCallback(() => {
    // Connection is handled by WalletModal via wagmi useConnect
  }, [])

  const connectSolana = useCallback(() => {
    // Connection is handled by WalletModal via solana wallet adapter select + connect
  }, [])

  const disconnectEvm = useCallback(() => {
    wagmiDisconnect()
  }, [wagmiDisconnect])

  const disconnectSolana = useCallback(() => {
    solanaDisconnect()
  }, [solanaDisconnect])

  const switchEvmChain = useCallback(async (chainId: number) => {
    await switchChain(wagmiConfig, { chainId: chainId as any })
  }, [])

  return {
    evmAddress,
    evmChainId,
    isEvmConnected,
    evmConnectorName: connector?.name,
    solanaAddress,
    isSolanaConnected,
    solanaWalletName: solanaWallet?.adapter.name,
    isConnecting,
    connectEvm,
    connectSolana,
    disconnectEvm,
    disconnectSolana,
    switchEvmChain,
  }
}
